import { Axios } from "../../Api/axios";

// Force / soft app update — backend: Admin\AppVersion\AdminAppVersionController
// (GET /Admin-app-versions, PUT /Admin-app-versions/{platform}).
//
// One row per platform. Builds BELOW min_build get the blocking "update
// required" page; builds below latest_build get the dismissible "update
// available" sheet (blocked too while force_latest is on). live_build is the
// build that is really downloadable in the store right now: the server
// refuses a min_build above it (users would be blocked with nothing to
// install). When it is empty the highest build seen >= 20 times in 24 h
// stands in for it.
//
// PUT answers 409 { warnings: [{code, message}] } for risky changes — resend
// with confirm: true after the admin has read them. 422 = refused.

export const AV_LANGS = [
  { code: "en", label: "English", native: "English", dir: "ltr" },
  { code: "ar", label: "Arabic", native: "العربية", dir: "rtl" },
  { code: "es", label: "Spanish", native: "Español", dir: "ltr" },
  { code: "pt", label: "Portuguese", native: "Português", dir: "ltr" },
  { code: "ru", label: "Russian", native: "Русский", dir: "ltr" },
  { code: "zh", label: "Chinese", native: "中文", dir: "ltr" },
];

export const PLATFORMS = [
  { key: "android", label: "Android", store: "Google Play" },
  { key: "ios", label: "iOS", store: "App Store" },
];

export const NOTES_MAX = 3;
export const NOTE_MAX_CHARS = 120;
export const TITLE_MAX = 120;
export const MESSAGE_MAX = 1000;
export const LIVE_MIN_HITS = 20;
export const BLOCK_WARN_SHARE = 0.2;

/// Texts the APP draws itself (buttons, labels) — same as the app's
/// assets/translations (app_update_*), used by the live preview only.
export const APP_STRINGS = {
  en: {
    update: "Update",
    later: "Later",
    checkAgain: "Check again",
    whatsNew: "What's new",
    safe: "Your account, videos and coins stay safe — updating takes about a minute.",
    version: (cur, next) => `Your version ${cur} · New version ${next}`,
    afterLive: "Update required after this LIVE",
  },
  ar: {
    update: "تحديث",
    later: "لاحقًا",
    checkAgain: "تحقق مرة أخرى",
    whatsNew: "ما الجديد",
    safe: "حسابك وفيديوهاتك وعملاتك في أمان — يستغرق التحديث نحو دقيقة.",
    version: (cur, next) => `إصدارك ${cur} · الإصدار الجديد ${next}`,
    afterLive: "التحديث مطلوب بعد انتهاء هذا البث المباشر",
  },
  es: {
    update: "Actualizar",
    later: "Más tarde",
    checkAgain: "Comprobar de nuevo",
    whatsNew: "Novedades",
    safe: "Tu cuenta, tus videos y tus monedas están a salvo: actualizar tarda alrededor de un minuto.",
    version: (cur, next) => `Tu versión ${cur} · Nueva versión ${next}`,
    afterLive: "Actualización necesaria al terminar este LIVE",
  },
  pt: {
    update: "Atualizar",
    later: "Mais tarde",
    checkAgain: "Verificar novamente",
    whatsNew: "Novidades",
    safe: "Sua conta, seus vídeos e suas moedas estão seguros — a atualização leva cerca de um minuto.",
    version: (cur, next) => `Sua versão ${cur} · Nova versão ${next}`,
    afterLive: "Atualização necessária após esta LIVE",
  },
  ru: {
    update: "Обновить",
    later: "Позже",
    checkAgain: "Проверить снова",
    whatsNew: "Что нового",
    safe: "Ваш аккаунт, видео и монеты в безопасности — обновление займёт около минуты.",
    version: (cur, next) => `Ваша версия ${cur} · Новая версия ${next}`,
    afterLive: "Обновление потребуется после этого эфира",
  },
  zh: {
    update: "更新",
    later: "稍后",
    checkAgain: "再次检查",
    whatsNew: "新功能",
    safe: "您的账号、视频和金币都很安全——更新大约只需一分钟。",
    version: (cur, next) => `您的版本 ${cur} · 新版本 ${next}`,
    afterLive: "本场直播结束后需要更新",
  },
};

export const listAppVersions = () => Axios.get("/Admin-app-versions").then((r) => r.data);

export const saveAppVersion = (platform, body) =>
  Axios.put(`/Admin-app-versions/${platform}`, body).then((r) => r.data);

/// Same rule as AppVersion::actionFor (server) and AppUpdateInfo.actionFor (app).
export function actionFor(cfg, build) {
  if (!cfg || !cfg.enabled || build === null || build === undefined) return "none";
  const min = Number(cfg.min_build) || 0;
  const latest = Number(cfg.latest_build) || 0;
  if (build < min || (cfg.force_latest && build < latest)) return "force";
  if (build < latest) return "soft";
  return "none";
}

export const totalHits = (seen = []) => seen.reduce((s, r) => s + (Number(r.hits) || 0), 0);

export const blockedHits = (seen = [], cfg) =>
  cfg ? seen.reduce((s, r) => (actionFor(cfg, Number(r.build)) === "force" ? s + (Number(r.hits) || 0) : s), 0) : 0;

export const newlyBlockedHits = (seen = [], before, after) =>
  seen.reduce((s, r) => {
    const b = Number(r.build);
    return actionFor(after, b) === "force" && (!before || actionFor(before, b) !== "force") ? s + (Number(r.hits) || 0) : s;
  }, 0);

export const maxSeenBuild = (seen = [], minHits = LIVE_MIN_HITS) =>
  seen.reduce((m, r) => (Number(r.hits) >= minHits && (m === null || Number(r.build) > m) ? Number(r.build) : m), null);

export const pct = (part, total) => (total > 0 ? Math.round((part * 1000) / total) / 10 : 0);

export const isHttpsUrl = (value) => {
  try {
    return new URL(String(value || "").trim()).protocol === "https:";
  } catch (e) {
    return false;
  }
};

export const fmtInt = (n) => (n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString("en-US"));

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

/// "3 min ago" / "5 h ago".
export const fmtAgo = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const min = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
};

/// Server row → form state (strings for inputs, every language present).
export function toForm(row) {
  const texts = (map) => Object.fromEntries(AV_LANGS.map(({ code }) => [code, (map || {})[code] || ""]));
  const notes = Object.fromEntries(
    AV_LANGS.map(({ code }) => {
      const list = ((row?.release_notes || {})[code] || []).slice(0, NOTES_MAX);
      while (list.length < NOTES_MAX) list.push("");
      return [code, list];
    })
  );
  const num = (v) => (v === null || v === undefined ? "" : String(v));
  return {
    min_build: num(row?.min_build ?? 0),
    min_version: row?.min_version || "",
    latest_build: num(row?.latest_build ?? 0),
    latest_version: row?.latest_version || "",
    live_build: num(row?.live_build),
    live_version: row?.live_version || "",
    force_latest: !!row?.force_latest,
    enabled: row?.enabled !== false,
    store_url: row?.store_url || "",
    title: texts(row?.title),
    message: texts(row?.message),
    force_message: texts(row?.force_message),
    release_notes: notes,
  };
}

/// Form state → PUT body (same shape the server stores).
export function toPayload(form) {
  const intOr = (v, fallback) => {
    const s = String(v ?? "").trim();
    if (s === "") return fallback;
    const n = Number(s);
    return Number.isInteger(n) && n >= 0 ? n : NaN;
  };
  const texts = (map) => {
    const out = {};
    AV_LANGS.forEach(({ code }) => {
      const v = String(map?.[code] || "").trim();
      if (v) out[code] = v;
    });
    return Object.keys(out).length ? out : null;
  };
  const notes = {};
  AV_LANGS.forEach(({ code }) => {
    const list = (form.release_notes?.[code] || []).map((s) => String(s || "").trim()).filter(Boolean);
    if (list.length) notes[code] = list;
  });
  const str = (v) => {
    const s = String(v || "").trim();
    return s === "" ? null : s;
  };
  return {
    min_build: intOr(form.min_build, 0),
    min_version: str(form.min_version),
    latest_build: intOr(form.latest_build, 0),
    latest_version: str(form.latest_version),
    live_build: intOr(form.live_build, null),
    live_version: str(form.live_version),
    force_latest: !!form.force_latest,
    enabled: !!form.enabled,
    store_url: str(form.store_url),
    title: texts(form.title),
    message: texts(form.message),
    force_message: texts(form.force_message),
    release_notes: Object.keys(notes).length ? notes : null,
  };
}

const FIELD_LABELS = {
  min_build: "Minimum build",
  min_version: "Minimum version",
  latest_build: "Latest build",
  latest_version: "Latest version",
  live_build: "Live build (store)",
  live_version: "Live version (store)",
  force_latest: "Force latest",
  enabled: "Prompts enabled",
  store_url: "Store link",
  title: "Title",
  message: "Sheet message",
  force_message: "Block page message",
  release_notes: "Release notes",
};

const show = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (Array.isArray(v)) return v.map((s) => `• ${s}`).join("\n");
  return String(v);
};

/// [{key, label, before, after}] of every field that differs.
export function diffPayload(before, after) {
  const rows = [];
  ["min_build", "min_version", "latest_build", "latest_version", "live_build", "live_version", "force_latest", "enabled", "store_url"].forEach(
    (k) => {
      if ((before?.[k] ?? null) !== (after?.[k] ?? null)) {
        rows.push({ key: k, label: FIELD_LABELS[k], before: show(before?.[k]), after: show(after?.[k]) });
      }
    }
  );
  ["title", "message", "force_message", "release_notes"].forEach((k) => {
    AV_LANGS.forEach(({ code }) => {
      const b = before?.[k]?.[code] ?? null;
      const a = after?.[k]?.[code] ?? null;
      if (JSON.stringify(b) !== JSON.stringify(a)) {
        rows.push({ key: `${k}.${code}`, label: `${FIELD_LABELS[k]} · ${code.toUpperCase()}`, before: show(b), after: show(a) });
      }
    });
  });
  return rows;
}
